import _ from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom-v5-compat";
import { getSubscriptionOrdersLists } from "../../../Account/utils/requestsTS";
import useToast from "../../../hooks/useToast";
import { useCompany } from "../../../Shared/components/CompanyProvider/CompanyProvider";
import { useCartSettings } from "../../../Shared/components/OrderSummary/OrderSummary.request";
import { useCurrencyLocalizer } from "../../../Shared/hooks/useCurrencyLocalizer";
import { useLogging } from "../../../Shared/hooks/useLogging/useLogging";
import { useSession } from "../../hooks/Session/useSession";
import { useOrderFlow } from "../../OrderFlowProvider/OrderFlowContext";
import { NavPortalRoots } from "../../utils/orderFlowUtils";
import { flattenSubscriptionDayGroups } from "../../utils/subscriptionUtils";
import { SubscriptionSummaryText, checkActiveAndFutureOrdersLength, compareSelections, getCurrentSelections, } from "./SubscriptionSummaryPage-utils";
export const useSubscriptionSummaryPage = (subscriptionLinkId, closeEditModal, isBalanceLoading) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const toast = useToast();
    const { store, balance } = useOrderFlow();
    const { loading, settings } = useCartSettings(store.model.Sku);
    const { datadogLog } = useLogging();
    const { openedFromMobileApp } = useCompany();
    const { SaveOrderFlowSession } = useSession();
    const [expandedAccordions, setExpandedAccordions] = useState([]);
    /**
     * Get and parse the query string params from the url.
     * If params.successUrl and params.failureUrl are present, we
     * use those as the redirect urls.
     */
    const { search } = useLocation();
    const params = new Proxy(new URLSearchParams(search), {
        get: (searchParams, prop) => searchParams.get(prop),
    });
    const portalRoot = document.getElementById(NavPortalRoots.EditSubscription);
    const [saveState, setSaveState] = useState({
        isSaving: false,
        isSaved: false,
    });
    const [subscriptionOrdersState, setSubscriptionOrdersState] = useState({
        orders: [],
        loading: true,
        orderGroups: [],
        selectedOrders: [],
        showUpcomingOrdersList: false,
        showPaymentForm: false,
    });
    const [ordersWithBalance, setOrdersWithBalance] = useState({
        orders: [],
        orderInfo: {},
    });
    useEffect(() => {
        let mounted = true;
        if (mounted)
            getSubscriptionOrdersLists(subscriptionLinkId, setSubscriptionOrdersState, store, toast, datadogLog, closeEditModal);
        return () => {
            mounted = false;
        };
    }, [subscriptionLinkId]);
    //If any omission has a category name, we must display by category
    const usesOmissionCategories = store.cart.omissions.find((omission) => omission.categoryName !== "");
    const displayOmissions = (settings === null || settings === void 0 ? void 0 : settings.omissions.display) && ((_b = (_a = store.cart) === null || _a === void 0 ? void 0 : _a.omissions) === null || _b === void 0 ? void 0 : _b.length) > 0;
    const groupedOmissions = Object.entries(_.groupBy(store.cart.omissions, item => item.categoryName || ""));
    const subscriptionSummaryTitle = (_c = store.model.SubscriptionSummaryHeaderText) !== null && _c !== void 0 ? _c : SubscriptionSummaryText.DEFAULT_HEADER;
    // If dateChangesTakeEffect is before today the order is cutoff so changes will apply next order
    const changeTakeEffectReplacementText = store.model.DateChangesTakeEffect == null || moment(store.model.DateChangesTakeEffect).isBefore(moment())
        ? SubscriptionSummaryText.YOUR_NEXT_ORDER
        : moment(store.model.DateChangesTakeEffect).utc().format("MM/DD");
    // Map products for use with the ProductSummary component
    const products = store.cart.products
        .filter((p) => p.quantity > 0)
        .map((product) => {
        return {
            name: product.productName,
            quantity: product.quantity,
            price: product.productPrice,
            id: product.productId,
            dayOfWeek: product.DayOfWeek,
            sale: product.salePrice,
        };
    });
    const productsForDisplay = _.groupBy(products, "dayOfWeek");
    // If the discountPerMeal setting is enabled, use the subtotal with line item discount
    const nextOrderSubtotal = (settings === null || settings === void 0 ? void 0 : settings.discountPerMeal.display)
        ? (_d = balance === null || balance === void 0 ? void 0 : balance.cost.subscription) === null || _d === void 0 ? void 0 : _d.adjustmentBreakdown.subtotalWithLineItemDiscount
        : (_e = balance === null || balance === void 0 ? void 0 : balance.cost.subscription) === null || _e === void 0 ? void 0 : _e.subtotal;
    // Get the localized currency text for the next order subtotal
    const nextOrderSubtotalCurrencyText = useCurrencyLocalizer(nextOrderSubtotal).localizedCurrencyText;
    const totalPrice = (_g = (_f = balance === null || balance === void 0 ? void 0 : balance.cost) === null || _f === void 0 ? void 0 : _f.subscription) === null || _g === void 0 ? void 0 : _g.total;
    const totalPriceCurrencyText = useCurrencyLocalizer(totalPrice).localizedCurrencyText;
    // Get the day groups for the subscription
    const dayGroups = flattenSubscriptionDayGroups((_h = balance === null || balance === void 0 ? void 0 : balance.cost.subscription) === null || _h === void 0 ? void 0 : _h.distributions, store.model.StartSunday);
    const hasDayGroups = dayGroups && dayGroups.length > 0 ? true : false;
    const headerText = subscriptionOrdersState.showUpcomingOrdersList
        ? SubscriptionSummaryText.UPDATE_ORDERS
        : subscriptionSummaryTitle;
    const showLoadingComponent = saveState.isSaving ||
        saveState.isSaved ||
        loading ||
        subscriptionOrdersState.loading ||
        isBalanceLoading ||
        !balance ||
        !settings;
    const hasActiveOrders = checkActiveAndFutureOrdersLength(subscriptionOrdersState.orderGroups);
    return {
        balance,
        datadogLog,
        dateChangesTakeEffectText: SubscriptionSummaryText.CHANGES_TAKE_EFFECT.replace("{placeHolder}", changeTakeEffectReplacementText),
        dayGroups,
        displayMealSize: settings === null || settings === void 0 ? void 0 : settings.mealSize.display,
        displayOmissions,
        displayRecipeType: (settings === null || settings === void 0 ? void 0 : settings.recipeType.display) && store.cart.recipeType,
        groupedOmissions,
        hasActiveOrders,
        hasDayGroups,
        haveSelectionsChangedFromOriginal: compareSelections(store.cart.originalSelections, getCurrentSelections(store.cart)),
        headerText,
        saveState,
        setSaveState,
        mealSize: store.cart.mealSize,
        nextOrderSubtotal,
        nextOrderSubtotalCurrencyText,
        omissions: store.cart.omissions,
        onlyShowingNextButton: store.cart.currentPage === 1 && store.model.IsEditFlow && store.cart.currentPage !== store.cart.totalPages,
        openedFromMobileApp,
        params,
        portalRoot,
        productsForDisplay,
        recipeType: store.cart.recipeType,
        SaveOrderFlowSession,
        setSubscriptionOrdersState,
        settings,
        shouldDisplayProducts: store.cart.products.some((p) => p.quantity > 0),
        showLoadingComponent,
        store,
        subscriptionOrdersState,
        subscriptionOrderUpdateAdvisoryNote: store.model.SubscriptionOrderUpdateAdvisoryNote,
        toast,
        totalPriceCurrencyText,
        usesOmissionCategories,
        expandedAccordions,
        setExpandedAccordions,
        ordersWithBalance,
        setOrdersWithBalance,
    };
};
